/**
 * Estado de filtros da aba Vendas do Dashboard.
 */

export const EMPTY_SALES_FILTER_STATE = {
  ids_status: [],
  ids_origens: [],
  ids_categorias: [],
  ids_subcategorias: [],
  ids_motivos: [],
  ids_usuarios: [],
  ids_agentes: [],
  availability: "",
  labels: {
    statuses: {},
    origins: {},
    categories: {},
    subcategories: {},
    motives: {},
    users: {},
    agents: {},
  },
};

const STATUS_OPTIONS = [
  { id: 2, label: "Concluída" },
  { id: 4, label: "Perdida" },
  { id: 5, label: "Cancelada" },
  { id: 0, label: "Pendente" },
  { id: 1, label: "Em Atendimento" },
  { id: 3, label: "Contato Futuro" },
];

export function getSalesStatusOptions() {
  return STATUS_OPTIONS;
}

function toPositiveIntArray(raw) {
  const list = Array.isArray(raw) ? raw : raw != null && raw !== "" ? String(raw).split(",") : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const n = Number(String(item ?? "").trim());
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

function toStatusIntArray(raw) {
  const list = Array.isArray(raw) ? raw : raw != null && raw !== "" ? String(raw).split(",") : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const n = Number(String(item ?? "").trim());
    if (!Number.isInteger(n) || n < 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

function toStringIdArray(raw) {
  const list = Array.isArray(raw) ? raw : raw != null && raw !== "" ? String(raw).split(",") : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const s = String(item ?? "").trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function normalizeLabels(map) {
  if (!map || typeof map !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(map)) {
    const id = String(k).trim();
    const label = String(v ?? "").trim();
    if (!id || !label) continue;
    out[id] = label;
  }
  return out;
}

export function normalizeSalesFilterState(raw) {
  const s = raw && typeof raw === "object" ? raw : {};
  const labels = s.labels && typeof s.labels === "object" ? s.labels : {};
  const availability = String(s.availability || "").trim().toLowerCase();
  return {
    ids_status: toStatusIntArray(s.ids_status),
    ids_origens: toPositiveIntArray(s.ids_origens),
    ids_categorias: toPositiveIntArray(s.ids_categorias),
    ids_subcategorias: toPositiveIntArray(s.ids_subcategorias),
    ids_motivos: toPositiveIntArray(s.ids_motivos),
    ids_usuarios: toPositiveIntArray(s.ids_usuarios),
    ids_agentes: toStringIdArray(s.ids_agentes),
    availability: availability === "imediata" || availability === "sob-encomenda" ? availability : "",
    labels: {
      statuses: normalizeLabels(labels.statuses),
      origins: normalizeLabels(labels.origins),
      categories: normalizeLabels(labels.categories),
      subcategories: normalizeLabels(labels.subcategories),
      motives: normalizeLabels(labels.motives),
      users: normalizeLabels(labels.users),
      agents: normalizeLabels(labels.agents),
    },
  };
}

export function countSalesFilterCriteria(state) {
  const s = normalizeSalesFilterState(state);
  let n = 0;
  if (s.ids_status.length) n += 1;
  if (s.ids_origens.length) n += 1;
  if (s.ids_categorias.length || s.ids_subcategorias.length) n += 1;
  if (s.ids_motivos.length) n += 1;
  if (s.ids_usuarios.length || s.ids_agentes.length) n += 1;
  if (s.availability) n += 1;
  return n;
}

export function salesFilterStateToQueryParams(state) {
  const s = normalizeSalesFilterState(state);
  const params = {};
  if (s.ids_status.length) params.ids_status = s.ids_status;
  if (s.ids_origens.length) params.ids_origens = s.ids_origens;
  if (s.ids_categorias.length) params.ids_categorias = s.ids_categorias;
  if (s.ids_subcategorias.length) params.ids_subcategorias = s.ids_subcategorias;
  if (s.ids_motivos.length) params.ids_motivos = s.ids_motivos;
  if (s.ids_usuarios.length) params.ids_usuarios = s.ids_usuarios;
  if (s.ids_agentes.length) params.ids_agentes = s.ids_agentes;
  if (s.availability) params.availability = s.availability;
  return params;
}

export function buildSalesFilterSummaries(state, t) {
  const s = normalizeSalesFilterState(state);
  const items = [];
  const tr = (key, fallback, vars) =>
    t(`dashboardPage.salesFilters.${key}`, { defaultValue: fallback, ...vars });

  s.ids_status.forEach((id) => {
    const fallback = STATUS_OPTIONS.find((o) => o.id === id)?.label || `#${id}`;
    const label = s.labels.statuses[String(id)] || fallback;
    items.push({
      key: `status:${id}`,
      label,
      title: tr("chipStatusTooltip", "Status: {{value}}", { value: label }),
      tone: "emphasis",
    });
  });

  s.ids_origens.forEach((id) => {
    const label = s.labels.origins[String(id)] || `#${id}`;
    items.push({
      key: `origin:${id}`,
      label,
      title: tr("chipOriginTooltip", "Canal: {{value}}", { value: label }),
      tone: "emphasis",
    });
  });

  s.ids_categorias.forEach((id) => {
    const label = s.labels.categories[String(id)] || `#${id}`;
    items.push({
      key: `cat:${id}`,
      label,
      title: tr("chipCategoryTooltip", "Categoria: {{value}}", { value: label }),
    });
  });

  s.ids_subcategorias.forEach((id) => {
    const label = s.labels.subcategories[String(id)] || `#${id}`;
    items.push({
      key: `sub:${id}`,
      label,
      title: tr("chipSubcategoryTooltip", "Subcategoria: {{value}}", { value: label }),
    });
  });

  s.ids_motivos.forEach((id) => {
    const label = s.labels.motives[String(id)] || `#${id}`;
    items.push({
      key: `motivo:${id}`,
      label,
      title: tr("chipMotiveTooltip", "Motivo de perda: {{value}}", { value: label }),
    });
  });

  const assigneeCount = s.ids_usuarios.length + s.ids_agentes.length;
  if (assigneeCount > 0) {
    items.push({
      key: "assignees",
      label: tr("chipAssigneesShort", "{{count}} responsável(is)", { count: assigneeCount }),
      title: tr("chipAssignees", "Responsáveis selecionados: {{count}}", { count: assigneeCount }),
    });
  }

  if (s.availability) {
    const label =
      s.availability === "imediata"
        ? tr("availabilityImmediate", "Disponibilidade imediata")
        : tr("availabilityOrder", "Sob encomenda");
    items.push({
      key: "availability",
      label,
      title: label,
    });
  }

  return items;
}
